import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  alpha,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  PersonOff as PersonOffIcon,
  HourglassEmpty as HourglassEmptyIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import api from '../../services/api';

const HistoricoSenhas = ({ senhasProcessadas, customTheme, user, forceRefresh }) => {
  const [senhasAtendidas, setSenhasAtendidas] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  
  // Função para buscar as senhas atendidas pelo usuário logado hoje
  const buscarSenhasAtendidas = async () => {
    if (!user || !user.id) return;
    
    setCarregando(true);
    setErro(null);
    
    try {
      const response = await api.get(`/senhas/atendente/${user.id}`);
      
      if (response.data.success) {
        setSenhasAtendidas(response.data.data);
      } else {
        console.error('Erro ao buscar senhas atendidas:', response.data.message);
        setErro('Falha ao carregar histórico');
      }
    } catch (error) {
      console.error('Erro ao comunicar com o servidor:', error);
      setErro('Erro de conexão');
    } finally {
      setCarregando(false);
    }
  };
  
  // Buscar senhas atendidas ao montar o componente e sempre que o usuário mudar
  useEffect(() => {
    buscarSenhasAtendidas();
    
    // Configurar intervalo para atualização a cada 30 segundos
    const intervalo = setInterval(buscarSenhasAtendidas, 30000);
    
    // Limpar intervalo quando o componente for desmontado
    return () => clearInterval(intervalo);
  }, [user]);

  // Forçar atualização quando forceRefresh mudar
  useEffect(() => {
    if (forceRefresh) {
      buscarSenhasAtendidas();
    }
  }, [forceRefresh]);
  
  // Combinar senhas do histórico local (atual) com as do servidor (hoje)
  const todasSenhas = [...senhasAtendidas, ...senhasProcessadas].reduce((acc, senha) => {
    // Verifica se a senha já existe no acumulador pelo ID
    const existente = acc.findIndex(s => s.id === senha.id);
    
    // Se não existir, adiciona ao acumulador
    if (existente === -1) {
      acc.push(senha);
    }
    // Se existir e a senha atual for do servidor (tem chamada_em), substitui
    else if (senha.chamada_em) {
      acc[existente] = senha;
    }
    
    return acc;
  }, []);
  
  // Formatar hora para exibição
  const formatarHora = (data) => {
    if (!data) return '';
    
    try {
      const dataObj = new Date(data);
      return format(dataObj, 'HH:mm', { locale: pt });
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return '';
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        height: '100%',
        maxHeight: { xs: '400px', md: '500px' },
        display: 'flex',
        flexDirection: 'column',
        border: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: alpha(customTheme.palette.grey[500], 0.1),
          bgcolor: alpha(customTheme.palette.grey[500], 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <HistoryIcon sx={{ color: customTheme.palette.grey[700], mr: 1.5 }} />
          <Typography variant="subtitle1" fontWeight={600}>
            Histórico de Hoje
          </Typography>
        </Box>
        <Chip
          label={`${todasSenhas.length} ${todasSenhas.length === 1 ? 'senha' : 'senhas'}`}
          size="small"
          color="default"
          variant="outlined"
          sx={{ fontWeight: 500 }}
        />
      </Box>

      <Box sx={{ overflow: 'auto', flexGrow: 1, maxHeight: 'calc(100% - 56px)' }}>
        {carregando && todasSenhas.length === 0 ? (
          <Box
            sx={{
              p: 3,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}
          >
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Carregando histórico...
            </Typography>
          </Box>
        ) : erro ? (
          <Box
            sx={{
              p: 3,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}
          >
            <ErrorOutlineIcon
              sx={{
                fontSize: 40,
                mb: 2,
                color: customTheme.palette.error.main
              }}
            />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Erro
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {erro}
            </Typography>
          </Box>
        ) : todasSenhas.length === 0 ? (
          <Box
            sx={{
              p: 3,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}
          >
            <HistoryIcon
              sx={{
                fontSize: 40,
                mb: 2,
                color: alpha(customTheme.palette.grey[500], 0.5)
              }}
            />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Sem Atendimentos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Nenhuma senha atendida hoje.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {todasSenhas.slice().sort((a, b) => {
              // Ordenar por data de finalização (mais recente primeiro)
              const dataA = a.finalizada_em || a.chamada_em || new Date();
              const dataB = b.finalizada_em || b.chamada_em || new Date();
              return new Date(dataB).getTime() - new Date(dataA).getTime();
            }).map((senha, index) => (
              <React.Fragment key={`${senha.id}-${index}`}>
                {index > 0 && <Divider component="li" />}
                <ListItem
                  sx={{
                    py: 1.5,
                    px: 2
                  }}
                >
                  <Box
                    sx={{
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: senha.resultado === 'Não Compareceu'
                        ? alpha(customTheme.palette.warning.main, 0.1)
                        : senha.resultado === 'Em Atendimento'
                          ? alpha(customTheme.palette.info.main, 0.1)
                          : alpha(customTheme.palette.success.main, 0.1),
                      color: senha.resultado === 'Não Compareceu'
                        ? customTheme.palette.warning.main
                        : senha.resultado === 'Em Atendimento'
                          ? customTheme.palette.info.main
                          : customTheme.palette.success.main
                    }}
                  >
                    {senha.resultado === 'Não Compareceu' ? (
                      <PersonOffIcon fontSize="small" />
                    ) : senha.resultado === 'Em Atendimento' ? (
                      <HourglassEmptyIcon fontSize="small" />
                    ) : (
                      <CheckCircleIcon fontSize="small" />
                    )}
                  </Box>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                          variant="subtitle2"
                          component="span"
                          sx={{
                            fontWeight: 600
                          }}
                        >
                          {senha.numero}
                        </Typography>
                        <Typography
                          variant="caption"
                          component="span"
                          sx={{
                            ml: 1,
                            color: 'text.secondary',
                            fontWeight: 500
                          }}
                        >
                          • {senha.tipo}
                        </Typography>
                        <Typography
                          variant="caption"
                          component="span"
                          sx={{
                            ml: 1,
                            color: 'text.secondary'
                          }}
                        >
                          {senha.chamada_em && `Chamada: ${formatarHora(senha.chamada_em)}`}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <Chip
                          label={senha.resultado}
                          size="small"
                          color={senha.resultado === 'Não Compareceu' 
                            ? 'warning' 
                            : senha.resultado === 'Em Atendimento' 
                              ? 'info' 
                              : 'success'}
                          variant="outlined"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            '& .MuiChip-label': {
                              px: 0.8
                            }
                          }}
                        />
                        {senha.preferencial && (
                          <Chip
                            label="Preferencial"
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{
                              ml: 1,
                              height: 20,
                              fontSize: '0.65rem',
                              '& .MuiChip-label': {
                                px: 0.8
                              }
                            }}
                          />
                        )}
                        {senha.guiche && (
                          <Chip
                            label={`Guichê ${senha.guiche}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{
                              ml: 1,
                              height: 20,
                              fontSize: '0.65rem',
                              '& .MuiChip-label': {
                                px: 0.8
                              }
                            }}
                          />
                        )}
                        {senha.servicos_adicionais && senha.servicos_adicionais.length > 0 && (
                          <Tooltip 
                            title={
                              <Box>
                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                  Serviços Adicionais:
                                </Typography>
                                {senha.servicos_adicionais.map((servico, index) => (
                                  <Typography key={index} variant="caption" sx={{ display: 'block' }}>
                                    • {servico}
                                  </Typography>
                                ))}
                              </Box>
                            }
                            arrow
                          >
                            <Chip
                              label={`+${senha.servicos_adicionais.length} serviço${senha.servicos_adicionais.length > 1 ? 's' : ''}`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{
                                ml: 1,
                                height: 20,
                                fontSize: '0.65rem',
                                cursor: 'help',
                                '& .MuiChip-label': {
                                  px: 0.8
                                }
                              }}
                            />
                          </Tooltip>
                        )}
                        {senha.tempoAtendimento > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              ml: 'auto',
                              color: 'text.secondary'
                            }}
                          >
                            {Math.floor(senha.tempoAtendimento / 60)}min {senha.tempoAtendimento % 60}s
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>
    </Paper>
  );
};

export default HistoricoSenhas; 